import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { fmtCurrency, fmtDateTime, StatusBadge, STATUS_CONFIG } from "@/utils/format";
import { SIPARIS } from "@/constants/testIds";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, FileSpreadsheet } from "lucide-react";
import BackorderBadge from "@/components/BackorderBadge";

const STATUS_TABS = ["tumu", "beklemede", "onaylandi", "tamamlandi", "iptal"];
const STATUS_LABELS = { tumu: "Tümü", ...Object.fromEntries(Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.label])) };
const STATUS_ACTIONS = {
  beklemede: ["onaylandi", "iptal"],
  onaylandi: ["tamamlandi", "iptal"],
  tamamlandi: [],
  iptal: [],
};

export default function SiparislerAdmin() {
  const [siparisler, setSiparisler] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [activeTab, setActiveTab] = useState("tumu");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (activeTab !== "tumu") params.durum = activeTab;
      const r = await api.get("/siparisler/", { params });
      setSiparisler(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [activeTab]);

  const handleDurumUpdate = async (siparis_id, durum) => {
    setUpdatingId(siparis_id);
    try {
      await api.put(`/siparisler/${siparis_id}/durum`, { durum });
      toast.success(`Durum: ${STATUS_LABELS[durum]}`);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleExcelExport = async (siparis) => {
    setExportingId(siparis.id);
    try {
      const response = await api.get(`/siparisler/${siparis.id}/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${siparis.siparis_no || "siparis"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${siparis.siparis_no} Excel'e aktarıldı`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Siparişler</h1>
        <p className="text-sm text-slate-500 mt-0.5">{total} sipariş</p>
      </div>

      {/* Status Tabs */}
      <div data-testid={SIPARIS.statusFilter} className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {STATUS_TABS.map((s) => (
          <button key={s} onClick={() => setActiveTab(s)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === s ? "border-[#0D1B3E] text-[#0D1B3E]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {STATUS_LABELS[s] || s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="table-container">
          <table data-testid={SIPARIS.listTable} className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                {["Sipariş No", "Tarih", "Cari", "Toplam", "Durum", "İşlem"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Yükleniyor...</td></tr>
              ) : siparisler.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Sipariş bulunamadı</td></tr>
              ) : siparisler.map((s) => (
                <>
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-sm font-medium text-[#0D1B3E]">{s.siparis_no}</span>
                        <BackorderBadge order={s} />
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(s.tarih)}</td>
                    <td className="px-4 py-3 text-slate-700">{s.cari_unvan}</td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-slate-800">{fmtCurrency(s.toplam_tutar)}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.durum} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          disabled={exportingId === s.id}
                          onClick={() => handleExcelExport(s)}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded border border-emerald-600/30 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors disabled:opacity-50"
                          title="Siparişi Excel'e aktar"
                        >
                          <FileSpreadsheet size={13} />
                          {exportingId === s.id ? "Hazırlanıyor" : "Excel"}
                        </button>
                        {(STATUS_ACTIONS[s.durum] || []).map((nextStatus) => (
                          <button key={nextStatus} data-testid={`${SIPARIS.durumUpdate}-${s.id}`}
                            disabled={updatingId === s.id}
                            onClick={() => handleDurumUpdate(s.id, nextStatus)}
                            className="text-xs px-2.5 py-1 rounded border border-[#0D1B3E]/30 text-[#0D1B3E] hover:bg-[#0D1B3E] hover:text-white transition-colors disabled:opacity-50">
                            {STATUS_LABELS[nextStatus]}
                          </button>
                        ))}
                        <ChevronDown size={14} className={`text-slate-400 transition-transform ${expandedId === s.id ? "rotate-180" : ""}`} />
                      </div>
                    </td>
                  </tr>
                  {expandedId === s.id && s.satirlar && (
                    <tr key={`${s.id}-detail`}>
                      <td colSpan={6} className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Sipariş Kalemleri</div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left py-1">Stok Kodu</th>
                              <th className="text-left py-1">Ürün Adı</th>
                              <th className="text-right py-1">Miktar</th>
                              <th className="text-right py-1">Birim Fiyat (KDV Hariç)</th>
                              <th className="text-right py-1">Toplam</th>
                            </tr>
                          </thead>
                          <tbody>
                            {s.satirlar.map((sat, idx) => (
                              <tr key={`${s.id}-satir-${idx}`} className={`border-t border-slate-200 ${Number(sat.bakiyeye_alinan || 0) > 0 ? "bg-amber-50/80" : ""}`}>
                                <td className="py-1.5 font-mono">{sat.stok_kodu}</td>
                                <td className="py-1.5">{sat.stok_adi}</td>
                                <td className="py-1.5 text-right tabular-nums">
                                  <span>{sat.miktar}</span>
                                  {Number(sat.bakiyeye_alinan || 0) > 0 && <span className="ml-2 inline-flex rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-700">{sat.bakiyeye_alinan} adet bakiye</span>}
                                </td>
                                <td className="py-1.5 text-right tabular-nums">{fmtCurrency(Number(sat.birim_fiyat || 0) / 1.20)}</td>
                                <td className="py-1.5 text-right tabular-nums font-semibold">{fmtCurrency(sat.toplam)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {s.not && <p className="mt-2 text-xs text-slate-500 italic">Not: {s.not}</p>}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">{total} kayıt</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronLeft size={15} /></button>
            <span className="text-xs text-slate-600 px-2">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="p-1.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
